import { DocumentProps } from '~/editor/document/document.interfaces'
import { imageLoader } from '~/editor/image/image'
import Page from '~/editor/page/page'
import { Page as PageData } from '~/types/comic/page'
import { ActiveSelection, Canvas, FabricObject, Point, TPointerEventInfo, util } from 'fabric'
import { resetTranslations, scaleTranslations } from '~/services/current-document/translations'
import { history } from '~/editor/history'
import { scaleTypesetTexts } from '~/services/current-document/typeset-texts'
import { Job } from '~/types/comic/chapter'
import MouseHandler from '~/editor/mouse-handler/mouse-handler'
import createFromCoords from '~/editor/translation/create'
import { setLoadingState } from '~/services/loading/loading'

class Document extends EventTarget{
  canvas: Canvas
  canvasViewport: HTMLDivElement
  scrollableArea: HTMLDivElement
  width: number
  pages: Page[]
  hasFocus: boolean
  hasMouse: boolean = false
  documentHeight: number
  settings: {
    mode: Job
    readOnly: boolean
  }
  selection: ActiveSelection | null
  hasSelection: boolean = false
  linkedDocument?: Document
  transformMatrix = util.composeMatrix({})
  transform = {
    zoom: localStorage.getItem('zoom') ? parseFloat(localStorage.getItem('zoom')!) : 1,
    pan: new Point(0, 0),
    offset: new Point(0, 0)
  }
  panToggle = false
  isPanning = false
  resizeTimeout: number | null = null
  mouseHandler: MouseHandler | null = null
  private isManualScrolling = false
  private scrollSyncTimeout: number | null = null

  constructor({ canvasElement, canvasViewport, scrollableArea, settings }: DocumentProps){
    super()
    this.canvas = new Canvas(canvasElement, {
      selection: !settings.readOnly
    })
    this.canvasViewport = canvasViewport
    this.scrollableArea = scrollableArea
    this.canvas.imageSmoothingEnabled = true
    this.width = this.canvasViewport.offsetWidth
    this.documentHeight = 0
    this.settings = {
      mode: settings.mode,
      readOnly: settings.readOnly !== undefined ? settings.readOnly : false
    }
    this.selection = null
    this.pages = []
    this.hasFocus = !this.settings.readOnly
    history.clear()
    
    this.registerEventListeners()
  }

  async loadPages(pages: PageData[]){
    this.reset()
    const imageLoaders: Promise<HTMLImageElement>[] = []
    pages.forEach(page => {
      imageLoaders.push(imageLoader(page.url))
    })

    await Promise.all(imageLoaders).then(images => {
      // Create Fabric Images for each page
      images.forEach((image, index) => {
        const pageId = pages.find(entry => entry.url === image.src)?.id
        if(pageId){
          const page = new Page({
            document: this,
            image,
            id: pageId,
            index
          })
          this.pages.push(page)
          this.canvas.add(page.image)
          this.canvas.add(page.pageIndex)
          this.canvas.sendObjectToBack(page.image)
        }
      })

      // Scale at current zoom
      this.resizeCanvas()
      this.dispatchEvent(new Event('pagesready'))
    })
  }

  add(element: FabricObject | FabricObject[]) {
    const objects = Array.isArray(element) ? element : [element]
    this.canvas.add(...objects)
  }

  remove(element: FabricObject | FabricObject[]){
    const objects = Array.isArray(element) ? element : [element]
    this.canvas.remove(...objects)
  }

  reset(){
    this.pages = []
    if(this.settings.mode === 'translation' || this.settings.mode === 'proofreading') resetTranslations()
    this.canvas.clear()
  }

  scalePages(){
    this.documentHeight = 0

    this.pages.forEach(page => {
      // Update the scale according to canvas size & zoom
      // Offset each page after the last one
      page.resize()
      // Compute document height by adding all pages height
      this.documentHeight += page.image.getScaledHeight()
    })

    this.scrollableArea.style.height = `${this.documentHeight}px`

    scaleTranslations()
    scaleTypesetTexts()
    this.render()
  }

  nudgeSelection(x: number, y: number){
    const selection = this.canvas.getActiveObjects()
    selection.forEach(object => {
      object.set({
        left: object.left + x * 2,
        top: object.top + y * 2
      })
      object.fire('nudge')
    })
    this.render()
  }

  onSelection(){
    this.hasFocus = true
    const activeObject = this.canvas.getActiveObject()
    if (activeObject?.isType('activeselection')){
      this.selection = activeObject as ActiveSelection
      this.selection.lockRotation = true
      this.selection.setControlVisible('mtr', false)
    }else{
      this.selection = null
    }
  }

  onSelectionClear(){
    this.selection = null
  }

  onObjectMove(){
    const selection = this.canvas.getActiveObjects()
    if (selection.length > 1){
      selection.forEach(object => {
        object.fire('moving')
      })
    }
  }

  onObjectScale(){
    const selection = this.canvas.getActiveObjects()
    if(selection.length > 1){
      selection.forEach(object => {
        object.fire('scaling')
      })
    }
  }

  onObjectRotate(){
    const selection = this.canvas.getActiveObjects()
    if(selection.length > 1){
      selection.forEach(object => {
        object.fire('rotating')
      })
    }
  }

  onObjectMouseUp(){
    const selection = this.canvas.getActiveObjects()
    if(selection.length > 1){
      selection.forEach(object => {
        object.fire('mouseup')
      })
    }
  }

  onKeyDown = (event: KeyboardEvent) => {
    const chordKey = window.navigator.platform.includes('Mac') ? event.metaKey : event.ctrlKey
    
    // Action Keys
    if(!this.settings.readOnly && this.hasFocus){
      // Delete
      if(event.code === "Delete" || event.code === "Backspace"){
        console.log("onKeyDown: onDelete")
        event.preventDefault()
        this.canvas.getActiveObjects().forEach(object => {
          object.fire('delete')
        })
      }
      // Undo/Redo
      if(event.code === "KeyW" && chordKey && !event.shiftKey){
        event.preventDefault()
        history.undo()
      }
      if((event.code === "KeyY" && chordKey) || (event.code === "KeyW" && chordKey && event.shiftKey)){
        event.preventDefault()
        history.redo()
      }
      // Nudge
      if(event.code === "ArrowUp"){
        event.preventDefault()
        this.nudgeSelection(0, -1)
      }
      if(event.code === "ArrowDown"){
        event.preventDefault()
        this.nudgeSelection(0, 1)
      }
      if(event.code === "ArrowLeft"){
        event.preventDefault()
        this.nudgeSelection(-1, 0)
      }
      if(event.code === "ArrowRight"){
        event.preventDefault()
        this.nudgeSelection(1, 0)
      }
    }

    // Transform Keys (can work in read-only mode)
    if(this.hasMouse && this.hasFocus){
      if(event.code === "Space" && !chordKey){
        console.log("Space: Grab Hotkey toggled")
        event.preventDefault()
        this.panToggle = true
        this.canvas.selection = false
        this.canvas.setCursor("grab")
      }
    }
  }

  onKeyUp(event: KeyboardEvent){
    event.preventDefault()
    this.panToggle = false
    this.canvas.selection = true
    this.canvas.setCursor("crosshair")
  }

  getCurrentPage(){
    let currentPage = this.pages[0]
    this.pages.forEach(page => {
      if(page.image.top <= this.canvasViewport.scrollTop && page.image.top + page.image.getScaledHeight() >= this.canvasViewport.scrollTop){
        currentPage = page
      }
    })
    return currentPage
  }

  onMouseWheel(event: TPointerEventInfo<WheelEvent>){
    if(this.linkedDocument){
      this.syncScrollWithLinkedDocument(event.e.deltaY)
    } else {
        this.canvasViewport.scrollBy({ top: event.e.deltaY })
        }
      }

  private syncScrollWithLinkedDocument(deltaY: number) {
    if (!this.linkedDocument) return

    const currentPage = this.getCurrentPage()
    const linkedCurrentPage = this.linkedDocument.pages[currentPage.index]

    // Handle case where documents have different number of pages
    if (!linkedCurrentPage) {
      this.canvasViewport.scrollBy({ top: deltaY })
      return
    }

    const currentPageTop = currentPage.image.top * this.transform.zoom
    const linkedPageTop = linkedCurrentPage.image.top * this.linkedDocument.transform.zoom
    const currentScrollTop = this.canvasViewport.scrollTop
    const linkedScrollTop = this.linkedDocument.canvasViewport.scrollTop

    // Calculate relative positions within the current pages
    const currentRelativePosition = (currentScrollTop - currentPageTop) / (currentPage.image.getScaledHeight() * this.transform.zoom)
    const linkedRelativePosition = (linkedScrollTop - linkedPageTop) / (linkedCurrentPage.image.getScaledHeight() * this.linkedDocument.transform.zoom)

    // Check if pages are at the same level (same relative position within their respective pages)
    const positionDiff = Math.abs(currentRelativePosition - linkedRelativePosition)
    const isAligned = positionDiff < 0.1 // Allow small tolerance

    if (isAligned) {
      // Scroll both documents together
      this.canvasViewport.scrollBy({ top: deltaY })
      this.linkedDocument.canvasViewport.scrollBy({ top: deltaY })
    } else {
      // Pages are not aligned, sync them by scrolling the lagging document
      const targetLinkedScrollTop = linkedPageTop + (currentRelativePosition * linkedCurrentPage.image.getScaledHeight() * this.linkedDocument.transform.zoom)

      this.canvasViewport.scrollBy({ top: deltaY })
      this.linkedDocument.canvasViewport.scrollTo({ top: targetLinkedScrollTop })
    }
  }

  onScroll(){
    // Handle manual scrolling (scrollbar clicks, etc.)
    if (!this.isManualScrolling && this.linkedDocument) {
      this.isManualScrolling = true

      // Clear existing timeout
      if (this.scrollSyncTimeout) {
        clearTimeout(this.scrollSyncTimeout)
      }

      // Sync with linked document after a short delay
      this.scrollSyncTimeout = window.setTimeout(() => {
        this.syncManualScroll()
        this.isManualScrolling = false
        this.scrollSyncTimeout = null
      }, 50)
    }

    this.render()
  }

  private syncManualScroll() {
    if (!this.linkedDocument) return

    const currentPage = this.getCurrentPage()
    const linkedCurrentPage = this.linkedDocument.pages[currentPage.index]

    if (!linkedCurrentPage) return

    const currentPageTop = currentPage.image.top * this.transform.zoom
    const linkedPageTop = linkedCurrentPage.image.top * this.linkedDocument.transform.zoom
    const currentScrollTop = this.canvasViewport.scrollTop

    // Calculate relative position within the current page
    const currentRelativePosition = (currentScrollTop - currentPageTop) / (currentPage.image.getScaledHeight() * this.transform.zoom)

    // Set linked document to the same relative position
    const targetLinkedScrollTop = linkedPageTop + (currentRelativePosition * linkedCurrentPage.image.getScaledHeight() * this.linkedDocument.transform.zoom)

    // Temporarily mark linked document as manually scrolling to prevent infinite sync
    this.linkedDocument.isManualScrolling = true
    this.linkedDocument.canvasViewport.scrollTo({ top: targetLinkedScrollTop })

    setTimeout(() => {
      if (this.linkedDocument) {
        this.linkedDocument.isManualScrolling = false
      }
    }, 100)
  }

  render(){
    this.transformMatrix = util.composeMatrix({
      scaleX: this.transform.zoom,
      scaleY: this.transform.zoom,
      translateY: -1 * this.canvasViewport.scrollTop + this.transform.pan.y + this.transform.offset.y,
      translateX: (this.canvas.getWidth() - this.canvas.getWidth() * this.transform.zoom) / 2 + this.transform.pan.x + this.transform.offset.x
    })
    this.canvas.setViewportTransform(this.transformMatrix)
    this.canvas.renderAll()

    if(this.linkedDocument){
      this.linkedDocument.transform = this.transform
      this.linkedDocument.transformMatrix = util.composeMatrix({
        scaleX: this.transform.zoom,
        scaleY: this.transform.zoom,
        translateY: -1 * this.linkedDocument.canvasViewport.scrollTop + this.transform.pan.y + this.transform.offset.y,
        translateX: (this.canvas.getWidth() - this.canvas.getWidth() * this.transform.zoom) / 2 + this.transform.pan.x + this.transform.offset.x
      })
      this.linkedDocument.canvas.setViewportTransform(this.linkedDocument.transformMatrix)
      this.linkedDocument.canvas.renderAll()
    }
  }

  onResize(){
    if(this.resizeTimeout){
      clearTimeout(this.resizeTimeout)
    }else{
      setLoadingState({
        loading: true
      })
    }
    this.resizeTimeout = window.setTimeout(() => {
      this.resizeCanvas()
      this.resizeTimeout = null
      setLoadingState({
        loading: false
      })
    }, 1000)
  }

  resizeCanvas(){
    this.canvas.discardActiveObject()
    this.canvas.setDimensions({
      height: this.canvasViewport.offsetHeight,
      width: this.scrollableArea.offsetWidth
    })
    this.width = this.canvasViewport.offsetWidth
    this.scalePages()
    this.setZoom(this.transform.zoom)
  }

  scrollTo(top: number){
    this.canvasViewport.scrollTo({ top: top * this.transform.zoom })
    if(this.linkedDocument){
      this.linkedDocument.canvasViewport.scrollTo({ top: this.canvasViewport.scrollTop })
    }
  }

  setZoom(zoom: number){
    const zoomOrigin = new Point(
      this.canvas.getWidth() / 2,
      0
    )

    // Apply new zoom to canvas
    this.canvas.zoomToPoint(zoomOrigin, zoom)
    this.transform.zoom = zoom

    // Apply new zoom to linked document
    if (this.linkedDocument) {
      this.linkedDocument.canvas.zoomToPoint(zoomOrigin, zoom)
      this.linkedDocument.transform.zoom = zoom
    }

    // Store new zoom in local storage
    localStorage.setItem('zoom', zoom.toString())

    // Save scroll position in percentage
    const scrollPosition = this.canvasViewport.scrollTop / parseFloat(this.scrollableArea.style.height)

    // Resize scrollable viewport
    this.scrollableArea.style.height = `${this.documentHeight * zoom}px`

    // Scroll to the same position in the new zoom
    this.scrollTo(scrollPosition * this.documentHeight)

    // Render the document to compute the new transformMatrix with the new zoom
    this.render()
  }

  zoomIn(){
    this.setZoom(this.canvas.getZoom() * 1.1)
  }

  zoomOut() {
    this.setZoom(this.canvas.getZoom() * 0.9)
  }

  resetZoom(){
    this.transform.pan.setXY(0,0)
    this.setZoom(1)
  }

  prevPage() {
    const zoom = this.transform.zoom
    const scrollTop = this.canvasViewport.scrollTop
    const page = this.pages.findLast(page => 
      // We apply Math.floor to both values to avoid floating point errors
      Math.floor(page.image.top * zoom) < Math.floor(scrollTop)
    )
    if (page) {
      this.scrollTo(page.image.top)
    }
  }

  nextPage() {
    const zoom = this.transform.zoom
    const scrollTop = this.canvasViewport.scrollTop
    const page = this.pages.find(page => 
      Math.floor(page.image.top * zoom) > Math.floor(scrollTop)
    )
    if (page) {
      this.scrollTo(page.image.top)
    }
  }

  onSelectionBox(handler: MouseHandler) {
    if(this.canvas.getActiveObjects().length > 0){
      this.hasSelection = true
      return
    }
    if(this.hasSelection){
      this.hasSelection = false
      return
    }

    const scrollTop = this.canvasViewport.scrollTop
    const page = this.pages.find(page => {
      const pageBottomY = - scrollTop + page.image.top + page.image.getScaledHeight()
      return pageBottomY > handler.pointerStart.y
    })
    if (page) {
      createFromCoords({
        pointerStart: handler.pointerStart,
        pointerEnd: handler.pointerEnd,
        page,
        transformMatrix: this.canvas.viewportTransform
      })
    }
  }

  onMouseOver(){
    this.hasMouse = true
  }

  onMouseOut(){
    this.hasMouse = false
  }

  registerEventListeners() {
    this.mouseHandler = new MouseHandler(this)
    const canEditTranslations = !this.settings.readOnly && (this.settings.mode === 'translation' || this.settings.mode === 'proofreading')
    if (canEditTranslations){
      this.mouseHandler.onSelectionBox = (handler) => this.onSelectionBox(handler)
    }
    this.canvas.on('mouse:over', () => this.onMouseOver())
    this.canvas.on('mouse:out', () => this.onMouseOut())
    this.canvas.on('mouse:wheel', (event) => this.onMouseWheel(event))
    this.canvas.on('mouse:up', () => this.onObjectMouseUp())
    this.canvas.on('selection:created', () => this.onSelection())
    this.canvas.on('selection:updated', () => this.onSelection())
    this.canvas.on('selection:cleared', () => this.onSelectionClear())
    this.canvas.on('object:moving', () => this.onObjectMove())
    this.canvas.on('object:scaling', () => this.onObjectScale())
    this.canvas.on('object:rotating', () => this.onObjectRotate())
    this.canvasViewport.addEventListener('scroll', () => this.onScroll())
    window.addEventListener('resize', () => this.onResize())
    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', (event) => this.onKeyUp(event))
  }

  unregisterEventListeners(){
    // Clear any pending scroll sync timeout
    if (this.scrollSyncTimeout) {
      clearTimeout(this.scrollSyncTimeout)
    }

    this.mouseHandler?.unregisterEventListeners()
    this.canvas.off('mouse:wheel', (event) => this.onMouseWheel(event))
    this.canvas.off('mouse:up', () => this.onObjectMouseUp())
    this.canvas.off('selection:created', () => this.onSelection())
    this.canvas.off('selection:updated', () => this.onSelection())
    this.canvas.off('selection:cleared', () => this.onSelectionClear())
    this.canvas.off('object:moving', () => this.onObjectMove())
    this.canvas.off('object:scaling', () => this.onObjectScale())
    this.canvas.off('object:rotating', () => this.onObjectRotate())
    this.canvasViewport.removeEventListener('scroll', () => this.onScroll())
    window.removeEventListener('resize', () => this.onResize())
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup', (event) => this.onKeyUp(event))
  }
}

export default Document